import React, {
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  import {
    useNavigate,
    useSearchParams,
  } from "react-router-dom";
  
  import "./TradingView.css";
  import CandlestickChart from "./CandlestickChart";
  import websocket from "../services/websocket";

  /*
   * ==========================================
   * CANDLE CACHE
   * ==========================================
   *
   * Cache iko nje ya component ili data
   * ibaki hata component ikifanya re-render.
   *
   * Key mfano:
   * frxEURUSD-300
   * frxEURUSD-60
   */
  const candleCache = new Map();
  
  const TradingView = () => {
    const navigate =
      useNavigate();
  
    const [searchParams] =
      useSearchParams();
  
    const symbol =
      searchParams.get("symbol") ||
      "EURUSD";
  
    // ==========================================
    // STATES
    // ==========================================
  
    const [
      timeframe,
      setTimeframe,
    ] = useState("5m");
  
    const [
      chartType,
      setChartType,
    ] = useState("candles");
  
    const [
      showOHLC,
      setShowOHLC,
    ] = useState(false);
  
    const [
      price,
      setPrice,
    ] = useState("--");
  
    const [
      candles,
      setCandles,
    ] = useState([]);
  
    const [
      loading,
      setLoading,
    ] = useState(true);
  
    // ==========================================
    // TIMEFRAMES
    // ==========================================
  
    const timeframes =
      useMemo(
        () => [
          {
            label: "1m",
            seconds: 60,
          },
  
          {
            label: "5m",
            seconds: 300,
          },
  
          {
            label: "15m",
            seconds: 900,
          },
  
          {
            label: "30m",
            seconds: 1800,
          },
  
          {
            label: "1H",
            seconds: 3600,
          },
  
          {
            label: "4H",
            seconds: 14400,
          },
  
          {
            label: "1D",
            seconds: 86400,
          },
        ],
        []
      );
  
    // ==========================================
    // SELECTED TIMEFRAME
    // ==========================================
  
    const selectedTimeframe =
      useMemo(() => {
        return (
          timeframes.find(
            (item) =>
              item.label ===
              timeframe
          ) ||
          timeframes[1]
        );
      }, [
        timeframe,
        timeframes,
      ]);
  
    // ==========================================
    // WEBSOCKET
    // ==========================================
  
    useEffect(() => {
      const apiSymbol =
        "frx" + symbol;
  
      const granularity =
        selectedTimeframe.seconds;
  
      const cacheKey =
        `${apiSymbol}-${granularity}`;
  
      // ========================================
      // CHECK CACHE FIRST
      // ========================================
  
      const cached =
        candleCache.get(
          cacheKey
        );
  
      if (
        cached &&
        cached.length
      ) {
        /*
         * Kama timeframe hii ilishafunguliwa,
         * chart inaonekana immediately.
         */
        setCandles(cached);
  
        const last =
          cached[
            cached.length - 1
          ];
  
        if (
          last &&
          Number.isFinite(
            Number(
              last.close
            )
          )
        ) {
          setPrice(
            Number(
              last.close
            ).toFixed(5)
          );
        }
  
        setLoading(false);
      } else {
        /*
         * HATUFANYI:
         *
         * setCandles([]);
         * setPrice("--");
         *
         * Hii inazuia chart kupotea
         * kila timeframe ikibadilika.
         */
  
        setLoading(true);
      }
  
      // ========================================
      // CONNECT
      // ========================================
  
      websocket.connect();
  
      // ========================================
      // LISTENER
      // ========================================
  
      const unsubscribe =
        websocket.onTick(
          (data) => {
            // ==================================
            // ERROR
            // ==================================
  
            if (
              data.error
            ) {
              console.error(
                "Chart error:",
                data.error.code,
                data.error.message
              );
  
              setLoading(false);
  
              return;
            }
  
            // ==================================
            // HISTORICAL CANDLES
            // ==================================
  
            if (
              data.candles &&
              data.echo_req
                ?.ticks_history
            ) {
              const responseSymbol =
                data.echo_req
                  .ticks_history;
  
              const responseGranularity =
                Number(
                  data.echo_req
                    ?.granularity
                );
  
              // Symbol tofauti
              if (
                responseSymbol !==
                apiSymbol
              ) {
                return;
              }
  
              /*
               * Muhimu sana.
               *
               * Ukibadilisha 1m -> 5m kwa haraka,
               * response ya 1m inaweza kuchelewa.
               *
               * Hii inaizuia isiingie kwenye 5m.
               */
              if (
                Number.isFinite(
                  responseGranularity
                ) &&
                responseGranularity !==
                  granularity
              ) {
                return;
              }
  
              // ================================
              // FORMAT
              // ================================
  
              const formatted =
                data.candles
                  .map(
                    (
                      candle
                    ) => ({
                      time:
                        Number(
                          candle.epoch
                        ),
  
                      open:
                        Number(
                          candle.open
                        ),
  
                      high:
                        Number(
                          candle.high
                        ),
  
                      low:
                        Number(
                          candle.low
                        ),
  
                      close:
                        Number(
                          candle.close
                        ),
                    })
                  )
  
                  .filter(
                    (
                      candle
                    ) =>
                      Number.isFinite(
                        candle.time
                      ) &&
                      Number.isFinite(
                        candle.open
                      ) &&
                      Number.isFinite(
                        candle.high
                      ) &&
                      Number.isFinite(
                        candle.low
                      ) &&
                      Number.isFinite(
                        candle.close
                      )
                  )
  
                  .sort(
                    (
                      a,
                      b
                    ) =>
                      a.time -
                      b.time
                  );
  
              if (
                !formatted.length
              ) {
                setLoading(
                  false
                );
  
                return;
              }
  
              // ================================
              // REMOVE DUPLICATES
              // ================================
  
              const unique =
                [];
  
              formatted.forEach(
                (
                  candle
                ) => {
                  const previous =
                    unique[
                      unique.length -
                        1
                    ];
  
                  if (
                    previous &&
                    previous.time ===
                      candle.time
                  ) {
                    unique[
                      unique.length -
                        1
                    ] =
                      candle;
                  } else {
                    unique.push(
                      candle
                    );
                  }
                }
              );
  
              // ================================
              // LIMIT CANDLES
              // ================================
  
              const finalCandles =
                unique.length >
                150
                  ? unique.slice(
                      -150
                    )
                  : unique;
  
              // ================================
              // CACHE
              // ================================
  
              candleCache.set(
                cacheKey,
                finalCandles
              );
  
              // ================================
              // DISPLAY
              // ================================
  
              setCandles(
                finalCandles
              );
  
              const last =
                finalCandles[
                  finalCandles.length -
                    1
                ];
  
              if (last) {
                setPrice(
                  Number(
                    last.close
                  ).toFixed(
                    5
                  )
                );
              }
  
              setLoading(
                false
              );
  
              return;
            }
  
            // ==================================
            // LIVE OHLC CANDLE
            // ==================================
  
            if (
              data.ohlc
            ) {
              const ohlc =
                data.ohlc;
  
              const responseSymbol =
                ohlc.symbol ||
                ohlc
                  .underlying_symbol ||
                "";
  
              if (
                responseSymbol !==
                apiSymbol
              ) {
                return;
              }
  
              const liveCandle =
                {
                  time:
                    Number(
                      ohlc.open_time ||
                        ohlc.epoch
                    ),
  
                  open:
                    Number(
                      ohlc.open
                    ),
  
                  high:
                    Number(
                      ohlc.high
                    ),
  
                  low:
                    Number(
                      ohlc.low
                    ),
  
                  close:
                    Number(
                      ohlc.close
                    ),
                };
  
              // ================================
              // VALIDATE
              // ================================
  
              if (
                !Number.isFinite(
                  liveCandle.time
                ) ||
                !Number.isFinite(
                  liveCandle.open
                ) ||
                !Number.isFinite(
                  liveCandle.high
                ) ||
                !Number.isFinite(
                  liveCandle.low
                ) ||
                !Number.isFinite(
                  liveCandle.close
                )
              ) {
                return;
              }
  
              // ================================
              // PRICE
              // ================================
  
              setPrice(
                liveCandle.close.toFixed(
                  5
                )
              );
  
              // ================================
              // UPDATE CANDLE
              // ================================
  
              setCandles(
                (
                  oldCandles
                ) => {
                  let updated;
  
                  if (
                    !oldCandles.length
                  ) {
                    updated = [
                      liveCandle,
                    ];
                  } else {
                    updated = [
                      ...oldCandles,
                    ];
  
                    const lastIndex =
                      updated.length -
                      1;
  
                    const lastCandle =
                      updated[
                        lastIndex
                      ];
  
                    // ==========================
                    // SAME CANDLE
                    // ==========================
  
                    if (
                      lastCandle.time ===
                      liveCandle.time
                    ) {
                      updated[
                        lastIndex
                      ] =
                        liveCandle;
                    }
  
                    // ==========================
                    // NEW CANDLE
                    // ==========================
  
                    else if (
                      liveCandle.time >
                      lastCandle.time
                    ) {
                      updated.push(
                        liveCandle
                      );
                    }
  
                    // ==========================
                    // KEEP 150
                    // ==========================
  
                    if (
                      updated.length >
                      150
                    ) {
                      updated =
                        updated.slice(
                          -150
                        );
                    }
                  }
  
                  // Update cache
                  candleCache.set(
                    cacheKey,
                    updated
                  );
  
                  return updated;
                }
              );
  
              setLoading(
                false
              );
  
              return;
            }
  
            // ==================================
            // LIVE TICK PRICE
            // ==================================
  
            if (
              data.tick
            ) {
              const tickSymbol =
                data.tick
                  .symbol ||
                data.tick
                  .underlying_symbol ||
                "";
  
              if (
                tickSymbol !==
                apiSymbol
              ) {
                return;
              }
  
              const currentPrice =
                Number(
                  data.tick
                    .quote
                );
  
              if (
                Number.isFinite(
                  currentPrice
                )
              ) {
                setPrice(
                  currentPrice.toFixed(
                    5
                  )
                );
              }
            }
          }
        );
  
      // ========================================
      // REQUEST CANDLES
      // ========================================
  
      websocket.candles(
        apiSymbol,
        granularity
      );
  
      // ========================================
      // CLEANUP
      // ========================================
  
      return () => {
        unsubscribe();
      };
    }, [
      symbol,
      selectedTimeframe.seconds,
    ]);
  
    // ==========================================
    // SELL
    // ==========================================
  
    const handleSell =
      () => {
        if (
          price === "--"
        ) {
          return;
        }
  
        const numericPrice =
          Number(price);
  
        if (
          !Number.isFinite(
            numericPrice
          )
        ) {
          return;
        }
  
        navigate(
          "/confirm-order",
          {
            state: {
              symbol,
              side: "sell",
              price:
                numericPrice,
            },
          }
        );
      };
  
    // ==========================================
    // BUY
    // ==========================================
  
    const handleBuy =
      () => {
        if (
          price === "--"
        ) {
          return;
        }
  
        const numericPrice =
          Number(price);
  
        if (
          !Number.isFinite(
            numericPrice
          )
        ) {
          return;
        }
  
        navigate(
          "/confirm-order",
          {
            state: {
              symbol,
              side: "buy",
              price:
                numericPrice,
            },
          }
        );
      };
  
    // ==========================================
    // RETURN
    // ==========================================
  
    return (
      <div className="tradingview-page">
       
  
        {/* =====================================
            HEADER
        ====================================== */}
  
  <div className="tv-header">

{/* BACK */}
<button
  type="button"
  className="tv-back-btn"
  onClick={() => navigate(-1)}
  aria-label="Go back"
>
  ‹
</button>

{/* SYMBOL */}
<div className="symbol-info">
  <h2>{symbol}</h2>
  <span>Forex</span>
</div>

{/* CURRENT PRICE */}
<div className="current-price">
  {price}
</div>

</div>
  
        {/* =====================================
            TIMEFRAMES
        ====================================== */}
  
        <div className="timeframes">
  
          {timeframes.map(
            (item) => (
              <button
                type="button"
  
                key={
                  item.label
                }
  
                className={
                  timeframe ===
                  item.label
                    ? "active"
                    : ""
                }
  
                onClick={() =>
                  setTimeframe(
                    item.label
                  )
                }
              >
                {item.label}
              </button>
            )
          )}
  
        </div>
  
        {/* =====================================
            MAIN CHART
        ====================================== */}
  
        <div className="main-chart">
  
          {/* ===================================
              TOOLBAR
          ==================================== */}
  
          <div className="chart-toolbar">
  
            {/* SYMBOL + TIMEFRAME */}
  
            <div className="chart-title">
  
              <span>
                {symbol}
              </span>
  
              <span>
                {timeframe}
              </span>
  
            </div>
  
            {/* =================================
                CONTROLS
            ================================== */}
  
            <div className="chart-type-controls">
  
              {/* ===============================
                  OHLC
              ================================ */}
  
              <button
                type="button"
  
                title="Open High Low Close"
  
                aria-label="Show Open High Low Close"
  
                className={
                  showOHLC
                    ? "active"
                    : ""
                }
  
                onClick={() =>
                  setShowOHLC(
                    (
                      current
                    ) =>
                      !current
                  )
                }
              >
                <svg
                  viewBox="0 0 32 32"
                  className="chart-type-icon"
                >
                  <line
                    x1="16"
                    y1="3"
                    x2="16"
                    y2="29"
                  />
  
                  <rect
                    x="11"
                    y="9"
                    width="10"
                    height="13"
                    rx="1"
                  />
  
                  <line
                    x1="5"
                    y1="16"
                    x2="11"
                    y2="16"
                  />
  
                  <line
                    x1="21"
                    y1="16"
                    x2="27"
                    y2="16"
                  />
                </svg>
              </button>
  
              {/* ===============================
                  CANDLESTICK
              ================================ */}
  
              <button
                type="button"
  
                title="Candlestick"
  
                aria-label="Candlestick chart"
  
                className={
                  chartType ===
                  "candles"
                    ? "active"
                    : ""
                }
  
                onClick={() =>
                  setChartType(
                    "candles"
                  )
                }
              >
                <svg
                  viewBox="0 0 32 32"
                  className="chart-type-icon"
                >
                  <line
                    x1="7"
                    y1="4"
                    x2="7"
                    y2="28"
                  />
  
                  <rect
                    x="4"
                    y="10"
                    width="6"
                    height="9"
                  />
  
                  <line
                    x1="16"
                    y1="2"
                    x2="16"
                    y2="25"
                  />
  
                  <rect
                    x="13"
                    y="6"
                    width="6"
                    height="12"
                  />
  
                  <line
                    x1="25"
                    y1="7"
                    x2="25"
                    y2="29"
                  />
  
                  <rect
                    x="22"
                    y="13"
                    width="6"
                    height="10"
                  />
                </svg>
              </button>
  
              {/* ===============================
                  BAR
              ================================ */}
  
              <button
                type="button"
  
                title="Bar chart"
  
                aria-label="Bar chart"
  
                className={
                  chartType ===
                  "bars"
                    ? "active"
                    : ""
                }
  
                onClick={() =>
                  setChartType(
                    "bars"
                  )
                }
              >
                <svg
                  viewBox="0 0 32 32"
                  className="chart-type-icon"
                >
                  <line
                    x1="7"
                    y1="5"
                    x2="7"
                    y2="27"
                  />
  
                  <line
                    x1="3"
                    y1="11"
                    x2="7"
                    y2="11"
                  />
  
                  <line
                    x1="7"
                    y1="20"
                    x2="11"
                    y2="20"
                  />
  
                  <line
                    x1="16"
                    y1="3"
                    x2="16"
                    y2="25"
                  />
  
                  <line
                    x1="12"
                    y1="8"
                    x2="16"
                    y2="8"
                  />
  
                  <line
                    x1="16"
                    y1="17"
                    x2="20"
                    y2="17"
                  />
  
                  <line
                    x1="25"
                    y1="8"
                    x2="25"
                    y2="29"
                  />
  
                  <line
                    x1="21"
                    y1="14"
                    x2="25"
                    y2="14"
                  />
  
                  <line
                    x1="25"
                    y1="23"
                    x2="29"
                    y2="23"
                  />
                </svg>
              </button>
  
              {/* ===============================
                  AREA
              ================================ */}
  
              <button
                type="button"
  
                title="Area chart"
  
                aria-label="Area chart"
  
                className={
                  chartType ===
                  "area"
                    ? "active"
                    : ""
                }
  
                onClick={() =>
                  setChartType(
                    "area"
                  )
                }
              >
                <svg
                  viewBox="0 0 32 32"
                  className="chart-type-icon area-icon"
                >
                  <path
                    className="area-fill"
  
                    d="
                      M3 27
                      L3 23
                      L9 19
                      L14 21
                      L20 14
                      L25 11
                      L29 6
                      L29 27
                      Z
                    "
                  />
  
                  <polyline
                    points="
                      3,23
                      9,19
                      14,21
                      20,14
                      25,11
                      29,6
                    "
                  />
  
                  <line
                    x1="3"
                    y1="27"
                    x2="29"
                    y2="27"
                  />
                </svg>
              </button>
  
            </div>
  
            {/* =================================
                LIVE
            ================================== */}
  
            <span className="live-status">
              ● LIVE
            </span>
  
          </div>
  
          {/* ===================================
              CHART
          ==================================== */}
  
          <div className="candlestick-container">
  
            <CandlestickChart
              candles={
                candles
              }
  
              timeframe={
                timeframe
              }
  
              chartType={
                chartType
              }
  
              showOHLC={
                showOHLC
              }
            />
  
            {/* =================================
                LOADING OVERLAY
  
                Inaonekana tu kama hakuna
                candle hata moja.
            ================================== */}
  
            {loading &&
              candles.length ===
                0 && (
                <div className="chart-loading-overlay">
                  Loading{" "}
                  {symbol}
                  ...
                </div>
              )}
  
          </div>
  
          {/* ===================================
              QUICK TRADE
          ==================================== */}
  
          <div className="quick-trade-row">
  
            {/* SELL */}
  
            <button
              type="button"
  
              className="
                quick-trade-btn
                quick-sell
              "
  
              disabled={
                price === "--"
              }
  
              onClick={
                handleSell
              }
            >
              <span>
                SELL
              </span>
  
              <strong>
                {price !== "--"
                  ? Number(
                      price
                    ).toFixed(
                      5
                    )
                  : "--"}
              </strong>
            </button>
  
            {/* BUY */}
  
            <button
              type="button"
  
              className="
                quick-trade-btn
                quick-buy
              "
  
              disabled={
                price === "--"
              }
  
              onClick={
                handleBuy
              }
            >
              <span>
                BUY
              </span>
  
              <strong>
                {price !== "--"
                  ? Number(
                      price
                    ).toFixed(
                      5
                    )
                  : "--"}
              </strong>
            </button>
  
          </div>
  
        </div>
  
        {/* =====================================
            MARKET STRUCTURE
        ====================================== */}
  
        <div className="market-structure">
  
          <div className="structure-header">
  
            <h3>
              Market Structure
            </h3>
  
            <span>
              {timeframe}
            </span>
  
          </div>
  
          <div className="structure-grid">
  
            <div className="structure-box">
  
              <span>
                Trend
              </span>
  
              <strong>
                Analyzing...
              </strong>
  
            </div>
  
            <div className="structure-box">
  
              <span>
                Structure
              </span>
  
              <strong>
                --
              </strong>
  
            </div>
  
            <div className="structure-box">
  
              <span>
                Last High
              </span>
  
              <strong>
                --
              </strong>
  
            </div>
  
            <div className="structure-box">
  
              <span>
                Last Low
              </span>
  
              <strong>
                --
              </strong>
  
            </div>
  
          </div>
  
        </div>
  
      </div>
    );
  };
  
  export default TradingView;